import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug, sanitize } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, EmailTemplate, Participant, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

/**
 * The `nylas-confirmation-email` component is a UI component that allows users to set buffer time before and after an event.
 *
 * @part nce__tab-heading - The nylas-confirmation-email main tab heading
 * @part nce - The nylas-confirmation-email container
 * @part nce__header - The header of the event communication section
 * @part nce__body - The body of the event communication section
 * @part nce__confirmation-email-toggle--container - The confirmation email drawer toggle container
 * @part nce__input-image-url - The input image URL container
 * @part nce__summary - The confirmation card summary container
 */
@Component({
  tag: 'nylas-confirmation-email',
  styleUrl: 'nylas-confirmation-email.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasConfirmationEmail {
  @Element() host!: HTMLNylasConfirmationEmailElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;
  /**
   * @standalone
   * The confirmation email template data to display
   */
  @Prop() confirmationEmailTemplate: EmailTemplate = {} as EmailTemplate;
  /**
   * @standalone
   * List of participants
   */
  @Prop() participants: Participant[] = [];
  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'confirmation-email-template';
  /**
   * @standalone
   * The nylas branding
   */
  @Prop() nylasBranding: boolean = true;
  /**
   * Is the confirmation email card open
   */
  @Prop() isOpen: boolean = false;
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;
  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The confirmation email template state.
   */
  @State() confirmationEmail!: EmailTemplate;

  /**
   * The internal state to store the isOpen state.
   */
  @State() isConfirmationEmailOpen: boolean = this.isOpen;

  /**
   * The internal state to store the participants.
   */
  @State() participantsState: Participant[] = this.participants;

  /**
   * The error message to display when the image URL is invalid.
   */
  @State() invalidImageErrorMessage: string = i18next.t('nylasConfirmationEmail.error.invalidImageUrl');

  /**
   * This event is fired when the email reminders change.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
    valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void;
  }>;

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-confirmation-email', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-confirmation-email', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-confirmation-email', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-confirmation-email', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else {
      this.confirmationEmail = { ...this.confirmationEmailTemplate };
      if (typeof this.confirmationEmail?.show_nylas_branding === 'undefined' && this.nylasBranding) {
        this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: true };
      } else if (!this.nylasBranding) {
        this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: false };
      }
      this.isConfirmationEmailOpen = this.isOpen;
      this.participantsState = this.participants;
    }
  }

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-confirmation-email', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    debug('nylas-confirmation-email', 'selectedConfigurationChangedHandler', newValue);
    this.confirmationEmail = { ...(newValue?.scheduler?.email_template || this.confirmationEmailTemplate) };
    if (typeof this.confirmationEmail?.show_nylas_branding === 'undefined' && this.nylasBranding) {
      this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: true };
    } else if (!this.nylasBranding) {
      this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: false };
    }
    this.isConfirmationEmailOpen = !newValue?.event_booking?.reminders?.length;
    this.participantsState = newValue?.participants || this.participants;
  }

  @Watch('nylasBranding')
  nylasBrandingChangedHandler(newValue: boolean) {
    debug('nylas-confirmation-email', 'nylasBrandingChangedHandler', newValue);
    if (typeof this.confirmationEmail?.show_nylas_branding === 'undefined' && newValue) {
      this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: true };
    } else if (!newValue) {
      this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: false };
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    this.invalidImageErrorMessage = i18next.t('nylasConfirmationEmail.error.invalidImageUrl');
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasFormInputChanged')
  async nylasFormInputChangeHandler(event: CustomEvent<{ value: string; name: string; type?: string }>) {
    const { name, value } = event.detail;
    const type = event.detail?.type;
    if (name === 'confirmation-email-title') {
      this.confirmationEmail = { ...this.confirmationEmail, booking_confirmed: { ...(this.confirmationEmail?.booking_confirmed || {}), title: value } };
      this.internals.setFormValue(JSON.stringify(this.confirmationEmail), this.name);
      this.valueChanged.emit({ value: JSON.stringify(this.confirmationEmail), name: this.name });
    } else if (name === 'confirmation-email-body' && type === 'multi_line_text') {
      this.handleEmailBodyChange(event);
    }
  }

  @Listen('valueChanged')
  valueChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('nylas-confirmation-email', 'valueChangedHandler', event.detail);
    const { value, name } = event.detail;

    if (name === 'confirmation-email-logo') {
      this.confirmationEmail = { ...this.confirmationEmail, logo: value };
      this.valueChanged.emit({ value: JSON.stringify(this.confirmationEmail), name: this.name });
      if (typeof this.internals.setValidity === 'function' && typeof this.internals.setFormValue === 'function') {
        this.internals.setFormValue(JSON.stringify(this.confirmationEmail), this.name);
        this.internals.setValidity({});
      }
    }
  }

  @Listen('nylasFormInputImageUrlInvalid')
  async nylasFormInputImageUrlInvalidHandler(event: CustomEvent<{ value: string; name: string }>) {
    const { value, name } = event.detail;
    if (name === 'confirmation-email-logo' && typeof this.internals.setValidity === 'function') {
      this.internals.setValidity({ customError: true }, i18next.t('nylasConfirmationEmail.error.invalidImageUrl') || value);
    }
  }

  @Listen('nylasFormSwitchToggled')
  nyalsFormSwitchToggledHandler(event: CustomEvent<{ checked: boolean; name: string; label: boolean }>) {
    debug('nylas-confirmation-email', 'nyalsFormSwitchToggledHandler', event.detail);
    if (event.detail.name === 'confirmation-email-show-branding') {
      this.confirmationEmail = { ...this.confirmationEmail, show_nylas_branding: event.detail.checked };
      this.internals.setFormValue(JSON.stringify(this.confirmationEmail), this.name);
      this.valueChanged.emit({ value: JSON.stringify(this.confirmationEmail), name: this.name });
    }
  }

  updateConfirmationFormValue() {
    debug('nylas-confirmation-email', 'updateConfirmationFormValue');
    this.internals.setFormValue(JSON.stringify(this.confirmationEmail), this.name);
    this.valueChanged.emit({ value: JSON.stringify(this.confirmationEmail), name: this.name });
  }

  toggleConfirmationEmail() {
    this.isConfirmationEmailOpen = !this.isConfirmationEmailOpen;
  }

  handleEmailBodyChange(event) {
    const value = sanitize(event.detail.value);
    this.confirmationEmail = { ...this.confirmationEmail, booking_confirmed: { ...(this.confirmationEmail?.booking_confirmed || {}), body: value } };
    this.internals.setFormValue(JSON.stringify(this.confirmationEmail), this.name);
    this.valueChanged.emit({ value: JSON.stringify(this.confirmationEmail), name: this.name });
  }

  @RegisterComponent<NylasConfirmationEmail, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-confirmation-email',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.nylasBranding', 'nylasBranding'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      valueChanged: async (
        event: CustomEvent<{ value: string; name: string; valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { valueChanged } = event.detail;
        // If a handler is passed, call it.
        if (valueChanged) {
          valueChanged(event);
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const organizer = this.participantsState.find(p => p.is_organizer);
    const organizerName = organizer?.name || organizer?.email;

    return (
      <Host>
        <div class="nylas-confirmation-email" part="nce">
          <div class="header" part="nce__header">
            <div>
              <h3>
                {this.confirmationEmail?.booking_confirmed?.title || organizerName
                  ? i18next.t('nylasConfirmationEmail.headerTitle_withOrganizer', { organizerName: organizerName })
                  : i18next.t('nylasConfirmationEmail.headerTitle')}
              </h3>
              <p>{i18next.t('nylasConfirmationEmail.headerSubTitle')}</p>
            </div>
            {this.isConfirmationEmailOpen ? (
              <div class="confirmation-email-toggle" part="nce__confirmation-email-toggle--container">
                <span class={`chevron ${this.isConfirmationEmailOpen ? 'open' : 'closed'} `} onClick={() => this.toggleConfirmationEmail()}>
                  <chevron-icon width="24" height="24" />
                </span>
              </div>
            ) : (
              <button-component
                variant={'basic'}
                clickHandler={event => {
                  event.preventDefault();
                  this.toggleConfirmationEmail();
                }}
              >
                <edit-icon width="16" height="16"></edit-icon>
                {i18next.t('nylasConfirmationEmail.editButtonText')}
              </button-component>
            )}
          </div>

          {this.isConfirmationEmailOpen ? (
            <div class="nylas-confirmation-email__body" part="nce__body">
              <div class="nylas-confirmation-email__section">
                {this.nylasBranding && (
                  <div class="nylas-confirmation-email__row">
                    <label>
                      {i18next.t('nylasConfirmationEmail.nylasBranding.label')}
                      <span class="label-icon">
                        <tooltip-component>
                          <info-icon slot="tooltip-icon" />
                          <span slot="tooltip-content">{i18next.t('nylasConfirmationEmail.nylasBranding.tooltip.desc')}</span>
                        </tooltip-component>
                      </span>
                    </label>
                    <toggle-switch
                      exportparts="ts_label: nce__toggle-label, ts_input: nce_toggle-input, ts_slider: nce_toggle-slider"
                      name={`confirmation-email-show-branding`}
                      checked={this.confirmationEmail?.show_nylas_branding}
                      themeConfig={this.themeConfig}
                    />
                  </div>
                )}
                <div class="nylas-confirmation-email__row">
                  <div class="subsection">
                    <div class="input-container">
                      <label>
                        {i18next.t('nylasConfirmationEmail.companyLogoUrl.label')}
                        <span class="label-icon">
                          <tooltip-component>
                            <info-icon slot="tooltip-icon" />
                            <span slot="tooltip-content">{i18next.t('nylasConfirmationEmail.companyLogoUrl.tooltip.desc')}</span>
                          </tooltip-component>
                        </span>
                      </label>

                      {this.confirmationEmail && (
                        <input-image-url
                          name="confirmation-email-logo"
                          exportparts="iiu__logo-input-textfield: nce__input-image-url"
                          errorMessage={this.invalidImageErrorMessage}
                          imageUrl={this.confirmationEmail?.logo ?? ''}
                          themeConfig={this.themeConfig}
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div class="nylas-confirmation-email__row full-width-col">
                  <div class="subsection">
                    <h3>{i18next.t('nylasConfirmationEmail.emailMessage.header')}</h3>
                    <div class="input-container">
                      <label htmlFor="title">
                        {i18next.t('nylasConfirmationEmail.emailMessage.label')}
                        <span class="label-icon">
                          <tooltip-component>
                            <info-icon slot="tooltip-icon" />
                            <span slot="tooltip-content">{i18next.t('nylasConfirmationEmail.emailMessage.tooltip.desc')}</span>
                          </tooltip-component>
                        </span>
                      </label>
                      <input-component
                        name="confirmation-email-title"
                        maxLength={60}
                        placeholder={
                          organizerName
                            ? i18next.t('nylasConfirmationEmail.headerTitle_withOrganizer', { organizerName: organizerName })
                            : i18next.t('nylasConfirmationEmail.headerTitle')
                        }
                        themeConfig={this.themeConfig}
                        defaultValue={this.confirmationEmail?.booking_confirmed?.title ?? ''}
                      ></input-component>
                    </div>
                    <div class="input-container">
                      {/* <label htmlFor="title">Email body</label> */}
                      <textarea-component
                        label={i18next.t('nylasConfirmationEmail.emailMessage.additionalInfoLabel')}
                        name="confirmation-email-body"
                        id="confirmation-email-body"
                        placeholder={i18next.t('nylasConfirmationEmail.emailMessage.additionalInfoPlaceholder')}
                        tooltip={i18next.t('nylasConfirmationEmail.emailMessage.additionalInfoTooltip')}
                        maxLength={500}
                        defaultValue={this.confirmationEmail?.booking_confirmed?.body ?? ''}
                        themeConfig={this.themeConfig}
                      ></textarea-component>

                      {/* <textarea
                        id="description"
                        part="nrt__textarea"
                        name="confirmation-email-body"
                        placeholder='Default body'
                        value={this.confirmationEmail?.booking_confirmed?.body ?? ''}
                        maxlength="500"
                        rows={2}
                        onInput={e => this.handleEmailBodyChange(e)}
                      ></textarea> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div class="nylas-confirmation-email__summary" part="nce__summary">
              <p>
                <span class="summary-icon">
                  <person-clipboard-icon></person-clipboard-icon>
                </span>
                {i18next.t('nylasConfirmationEmail.allHosts')}
              </p>
              <p>
                <span class="summary-icon">
                  <clock-icon></clock-icon>
                </span>
                {i18next.t('nylasConfirmationEmail.immediatelyUponBooking')}
              </p>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
